import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Container,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    Collapse,
    Tooltip,
} from '@mui/material';
import {
    Hub as HubIcon,
    AddLink as AddLinkIcon,
    GridOn as GridOnIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    DonutLarge as DonutLargeIcon,
    PushPin as PushPinIcon,
    PushPinOutlined as PushPinOutlinedIcon,
    ReceiptLong as ReceiptLongIcon,
    Dashboard as DashboardIcon,
    AccountBalance as AccountsIcon,
    History as HistoryIcon,
    Menu as MenuIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    TrendingUp as HoldingsIcon,
    Timeline as TimelineIcon,
    ViewList as ConsolidatedIcon,
    Receipt as TransactionsIcon,
    Category as CategoryIcon,
    Group as GroupIcon,
    PieChart as BudgetIcon,
    Assessment as ReportsIcon,
    SwapVert as ImportExportIcon,
    Flag as GoalsIcon,
    Repeat as RepeatIcon,
    Rule as RuleIcon,
    ExpandLess,
    ExpandMore,
    Brightness4,
    Brightness7,
    ShowChart as PortfolioIcon,
    AutoAwesome as AutoAwesomeIcon,
    Receipt as ReceiptIcon,
    People as PeopleIcon,
    Payments as PaymentsIcon,
    CameraAlt as SnapshotsIcon,
    Settings as SettingsIcon,
    VpnKey as VpnKeyIcon,
    Description as DescriptionIcon,
    Extension as ExtensionIcon,
    Savings as SavingsIcon,
    LocalFireDepartment as LocalFireDepartmentIcon,
    EventNote as LifeTimelineIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuthStore } from '../stores/auth-store';
import { usePlaidAccess } from '../services/plaid-access-service';
import { useBaseCurrencySync } from '../hooks/useBaseCurrencySync';
import { useProfileSync } from '../hooks/useProfileSync';
import { RemoteEnvBanner } from './RemoteEnvBanner';
import { usePreferencesStore } from '../stores/preferences-store';
import { httpClient } from '../services/http-client';
import { useColorMode } from '../theme';
import { MobileBottomNav } from './MobileBottomNav';
import { QuickAddFAB } from './QuickAddFAB';
import { FolioMark } from './FolioMark';

interface NavItem {
    text: string;
    icon: React.ReactNode;
    path: string;
}

interface NavSection {
    title: string;
    icon: React.ReactNode;
    items: NavItem[];
}

interface MainLayoutProps {
    children: React.ReactNode;
}

const navSections: NavSection[] = [
    {
        title: 'Overview',
        icon: <DashboardIcon />,
        items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
            { text: 'Money Flow', icon: <HubIcon />, path: '/flow' },
            { text: 'Net Worth', icon: <AccountBalanceWalletIcon />, path: '/net-worth' },
            { text: 'Accounts', icon: <AccountsIcon />, path: '/accounts-browser' },
        ],
    },
    {
        title: 'Transactions',
        icon: <TransactionsIcon />,
        items: [
            { text: 'Transactions', icon: <TransactionsIcon />, path: '/transactions' },
            { text: 'Activity', icon: <HistoryIcon />, path: '/activity-feed' },
            { text: 'Recurring', icon: <RepeatIcon />, path: '/recurring-transactions' },
            { text: 'Categories', icon: <CategoryIcon />, path: '/categories' },
            { text: 'Rules', icon: <RuleIcon />, path: '/categorization-rules' },
            { text: 'Import/Export', icon: <ImportExportIcon />, path: '/import-export' },
        ],
    },
    {
        title: 'Planning',
        icon: <BudgetIcon />,
        items: [
            { text: 'Bills', icon: <ReceiptLongIcon />, path: '/bills' },
            { text: 'Payment Grid', icon: <GridOnIcon />, path: '/payment-grid' },
            { text: 'Budgets', icon: <BudgetIcon />, path: '/budgets' },
            { text: 'Goals', icon: <GoalsIcon />, path: '/goals' },
            { text: 'Contribution Room', icon: <SavingsIcon />, path: '/contribution-room' },
            { text: 'FIRE Planner', icon: <LocalFireDepartmentIcon />, path: '/fire' },
            { text: 'Timeline', icon: <LifeTimelineIcon />, path: '/timeline' },
            { text: 'Investment plan', icon: <DonutLargeIcon />, path: '/investment-plan' },
        ],
    },
    {
        title: 'Reports',
        icon: <ReportsIcon />,
        items: [
            { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
            { text: 'Activity', icon: <HistoryIcon />, path: '/activity' },
        ],
    },
    {
        title: 'Investments',
        icon: <HoldingsIcon />,
        items: [
            { text: 'Analysis', icon: <AutoAwesomeIcon />, path: '/analysis' },
            { text: 'Performance', icon: <TimelineIcon />, path: '/performance' },
            { text: 'Allocation', icon: <DonutLargeIcon />, path: '/allocation' },
            { text: 'Recommendations', icon: <AutoAwesomeIcon />, path: '/recommendations' },
            { text: 'Risk', icon: <DonutLargeIcon />, path: '/risk' },
            { text: 'Holdings', icon: <HoldingsIcon />, path: '/holdings' },
            { text: 'Portfolio (ACB)', icon: <PortfolioIcon />, path: '/portfolio' },
            { text: 'Dividends', icon: <PaymentsIcon />, path: '/dividends' },
            { text: 'Fees', icon: <ReceiptIcon />, path: '/fees' },
            { text: 'Consolidated', icon: <ConsolidatedIcon />, path: '/consolidated-holdings' },
        ],
    },
    {
        title: 'Settings',
        icon: <SettingsIcon />,
        items: [
            { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
            { text: 'AI analysis', icon: <AutoAwesomeIcon />, path: '/settings-ai' },
            { text: 'Accounts', icon: <AccountsIcon />, path: '/accounts' },
            { text: 'Snapshots', icon: <SnapshotsIcon />, path: '/snapshots' },
            { text: 'Household', icon: <GroupIcon />, path: '/household' },
            { text: 'API Tokens', icon: <VpnKeyIcon />, path: '/api-tokens' },
            { text: 'Extensions', icon: <ExtensionIcon />, path: '/extensions' },
            { text: 'API Docs', icon: <DescriptionIcon />, path: '/api-docs' },
        ],
    },
];

const NAV_COLLAPSED_KEY = 'navCollapsed';
const NAV_PINNED_KEY = 'navPinned';

/** Full width when expanded; icon-only rail when collapsed. */
const NAV_WIDTH = 272;
const NAV_RAIL = 76;

// Reason: Load collapsed state from localStorage; default all sections to collapsed
// except the section containing the current active route
function loadCollapsedState(pathname: string): Record<string, boolean> {
    try {
        const stored = localStorage.getItem(NAV_COLLAPSED_KEY);
        if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    // First visit: collapse all sections, expand only the one with the active route
    const state: Record<string, boolean> = {};
    for (const section of navSections) {
        const hasActive = section.items.some(
            (item) => pathname === item.path || pathname.startsWith(`${item.path}/`)
        );
        state[section.title] = !hasActive;
    }
    return state;
}

function saveCollapsedState(state: Record<string, boolean>) {
    try {
        localStorage.setItem(NAV_COLLAPSED_KEY, JSON.stringify(state));
    } catch { /* ignore */ }
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const theme = useTheme();
    const { toggleColorMode, mode } = useColorMode();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
        loadCollapsedState(location.pathname)
    );
    const { user, logout } = useAuthStore();
    useBaseCurrencySync();
    useProfileSync();

    // Pinned means "stay expanded". Unpinned collapses to an icon rail that
    // expands on hover, so the nav is reachable without giving up canvas width
    // — which matters most on the flow map.
    const [pinned, setPinned] = useState<boolean>(() => {
        try { return localStorage.getItem(NAV_PINNED_KEY) !== 'false'; } catch { return true; }
    });
    const [hoverNav, setHoverNav] = useState(false);
    const navOpen = pinned || hoverNav;
    const navWidth = navOpen ? NAV_WIDTH : NAV_RAIL;

    const togglePinned = useCallback(() => {
        setPinned((prev) => {
            const next = !prev;
            try { localStorage.setItem(NAV_PINNED_KEY, String(next)); } catch { /* ignore */ }
            return next;
        });
    }, []);
    const developerMode = usePreferencesStore((s) => s.developerMode);
    // Connections: platform operators, or paid households with their own
    // Plaid keys (#42). Until /plaid/access answers, fall back to is_admin.
    const plaidAccess = usePlaidAccess((s) => s.access);
    const refreshPlaidAccess = usePlaidAccess((s) => s.refresh);
    useEffect(() => { if (user) void refreshPlaidAccess(); }, [user?.id, refreshPlaidAccess]);
    const connectionsEnabled = plaidAccess ? plaidAccess.connections_enabled : Boolean(user?.is_admin);

    // Direct bank connections are operator-only (migration 058): the instance
    // shares one small pool of paid Plaid Items. The API rejects everyone else,
    // so showing the nav entry would only produce a link that always 403s.
    const sections = useMemo(() => {
        // is_admin, not is_super_admin: connecting a bank is a household
        // operator capability. Platform administration is a separate, narrower
        // role and must not be what unlocks this.
        // Platform administration is super-admin only, and separate from
        // the household-operator entry below.
        const withAdmin = user?.is_super_admin
            ? [...navSections, {
                  title: 'Platform',
                  icon: <SettingsIcon />,
                  items: [
                      { text: 'Users', icon: <PeopleIcon />, path: '/admin-users' },
                  ],
              }]
            : navSections;
        if (!connectionsEnabled) return withAdmin;
        return withAdmin.map((section) =>
            section.title !== 'Transactions'
                ? section
                : {
                      ...section,
                      items: [
                          ...section.items.slice(0, 4),
                          { text: 'Connections', icon: <AddLinkIcon />, path: '/connections' },
                          ...section.items.slice(4),
                      ],
                  }
        );
    }, [connectionsEnabled, user?.is_super_admin]);

    // Reason: Hide integration surfaces until the user opts into developer mode.
    const filteredNavSections = useMemo(() => {
        return sections.map((section) => {
            if (section.title === 'Settings' && !developerMode) {
                return {
                    ...section,
                    items: section.items.filter(
                        (item) => !['/api-tokens', '/extensions', '/api-docs'].includes(item.path)
                    ),
                };
            }
            return section;
        });
        // `sections` belongs in here. It is not a constant -- it gains the
        // Connections entry once the profile fetch reports the operator flag,
        // which happens AFTER first render. Memoising on developerMode alone
        // pinned this to the pre-fetch value, so the nav item was computed
        // correctly and then thrown away: the flag was true, `sections` had
        // Connections in it, and the screen still did not.
    }, [developerMode, sections]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        // Clears the REFRESH token too. Clearing only the access token left a
        // credential behind that silently restored the session on the next
        // 401 -- signing out did not sign you out.
        httpClient.clearStoredCredentials();
        logout();
        navigate({ to: '/login' });
    };

    const toggleSection = useCallback((title: string) => {
        setCollapsed(prev => {
            const next = { ...prev, [title]: !prev[title] };
            saveCollapsedState(next);
            return next;
        });
    }, []);

    const isItemActive = (path: string) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`);

    const isSectionActive = (section: NavSection) =>
        section.items.some(item => isItemActive(item.path));

    const renderDrawer = (open: boolean) => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
                px: open ? 2.5 : 1.5, py: 2, display: 'flex', alignItems: 'center',
                gap: 1.5, minHeight: 64,
            }}>
                <FolioMark size={36} title="FolioWorth" />
                {open && (
                    <>
                        <Typography variant="h6" fontWeight="bold" color="primary" noWrap sx={{ flexGrow: 1 }}>
                            FolioWorth
                        </Typography>
                        <Tooltip title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}>
                            <IconButton size="small" onClick={togglePinned} aria-label="Toggle sidebar pin">
                                {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>
            <Divider />
            <List sx={{ flexGrow: 1, px: 1, py: 1, overflow: 'auto' }}>
                {filteredNavSections.map((section) => {
                    const isOpen = !collapsed[section.title];
                    const sectionActive = isSectionActive(section);

                    return (
                        <React.Fragment key={section.title}>
                            <ListItem disablePadding sx={{ mt: 0.5 }}>
                                <ListItemButton
                                    onClick={() => toggleSection(section.title)}
                                    sx={{
                                        borderRadius: 'var(--radius-md)',
                                        py: 0.6,
                                        px: open ? 1.5 : 1,
                                        justifyContent: open ? 'flex-start' : 'center',
                                        color: sectionActive ? 'var(--accent-text)' : 'var(--text-faint)',
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: open ? 30 : 0, color: 'inherit',
                                        justifyContent: 'center',
                                    }}>
                                        {section.icon}
                                    </ListItemIcon>
                                    {open && (
                                        <>
                                            <ListItemText
                                                primary={section.title}
                                                primaryTypographyProps={{
                                                    fontSize: '0.6875rem',
                                                    fontWeight: 750,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.12em',
                                                }}
                                            />
                                            {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                        </>
                                    )}
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={open ? isOpen : true} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {section.items.map((item) => {
                                        const isActive = isItemActive(item.path);
                                        return (
                                            <ListItem key={item.text} disablePadding sx={{ mb: 0.15 }}>
                                                <Tooltip title={open ? '' : item.text} placement="right">
                                                    <ListItemButton
                                                        component={Link}
                                                        to={item.path}
                                                        selected={isActive}
                                                        onClick={isMobile ? handleDrawerToggle : undefined}
                                                        sx={{
                                                            borderRadius: 'var(--radius-md)',
                                                            py: 0.6,
                                                            pl: open ? 3 : 1,
                                                            pr: open ? 1.5 : 1,
                                                            justifyContent: open ? 'flex-start' : 'center',
                                                            '&.Mui-selected': {
                                                                background: 'var(--accent-soft)',
                                                                color: 'var(--accent-text)',
                                                                '& .MuiListItemIcon-root': { color: 'var(--accent-text)' },
                                                            },
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            minWidth: open ? 32 : 0,
                                                            justifyContent: 'center',
                                                            color: isActive ? 'inherit' : 'var(--text-dim)',
                                                        }}>
                                                            {item.icon}
                                                        </ListItemIcon>
                                                        {open && (
                                                            <ListItemText
                                                                primary={item.text}
                                                                primaryTypographyProps={{
                                                                    fontWeight: isActive ? 650 : 500,
                                                                    fontSize: '0.8125rem',
                                                                }}
                                                            />
                                                        )}
                                                    </ListItemButton>
                                                </Tooltip>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>
            <Divider />
            <Box sx={{ p: 1, pb: 1.5 }}>
                {!open && (
                    <Tooltip title="Pin sidebar open" placement="right">
                        <ListItemButton
                            onClick={togglePinned}
                            sx={{ borderRadius: 'var(--radius-md)', justifyContent: 'center', py: 0.75, mb: 0.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: 'var(--text-dim)' }}>
                                <PushPinOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                        </ListItemButton>
                    </Tooltip>
                )}
                <Tooltip title={open ? '' : 'Logout'} placement="right">
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--danger-text)',
                            justifyContent: open ? 'flex-start' : 'center',
                            py: 0.75,
                        }}
                    >
                        <ListItemIcon sx={{
                            minWidth: open ? 32 : 0, justifyContent: 'center', color: 'inherit',
                        }}>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        {open && (
                            <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.8125rem' }} />
                        )}
                    </ListItemButton>
                </Tooltip>
            </Box>
        </Box>
    );

    // Reason: the shell stays transparent so the theme's ambient mesh shows
    // through and the glass surfaces above it have something to refract.
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
            <RemoteEnvBanner />
            <AppBar
                position="fixed"
                sx={{
                    // Pushed down by the environment banner when one is shown.
                    // Resolves to 0px in production, where no banner renders.
                    top: 'var(--env-banner-h, 0px)',
                    width: { md: `calc(100% - ${navWidth}px)` },
                    ml: { md: `${navWidth}px` },
                    transition: 'width var(--dur-base) var(--ease-out), margin-left var(--dur-base) var(--ease-out)',
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={toggleColorMode} color="inherit">
                            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
                        </IconButton>
                        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            {user?.display_name || user?.email}
                        </Typography>
                        <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                <PersonIcon />
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                                    mt: 1.5,
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                        >
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                onMouseEnter={() => !pinned && setHoverNav(true)}
                onMouseLeave={() => setHoverNav(false)}
                sx={{
                    width: { md: navWidth },
                    flexShrink: { md: 0 },
                    transition: 'width var(--dur-base) var(--ease-out)',
                }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: NAV_WIDTH,
                            top: 'var(--env-banner-h, 0px)',
                            height: 'calc(100% - var(--env-banner-h, 0px))',
                        },
                    }}
                >
                    {renderDrawer(true)}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        // Reason: no bgcolor here — the theme's MuiDrawer override
                        // supplies the frosted-glass surface.
                        // Reason: the rail floats above content when it expands on
                        // hover, so the page never reflows under the cursor.
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: navWidth,
                            overflowX: 'hidden',
                            // Offset by the environment banner, which is fixed
                            // and would otherwise cover the logo. 0px in prod.
                            top: 'var(--env-banner-h, 0px)',
                            height: 'calc(100% - var(--env-banner-h, 0px))',
                            transition: 'width var(--dur-base) var(--ease-out)',
                            zIndex: (t) => t.zIndex.drawer + 2,
                        },
                    }}
                    open
                >
                    {renderDrawer(navOpen)}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    pb: { xs: 10, md: 3 },
                    width: { md: `calc(100% - ${navWidth}px)` },
                    mt: 8,
                    transition: 'width var(--dur-base) var(--ease-out)',
                }}
            >
                <Container maxWidth="xl">
                    {children}
                </Container>
            </Box>

            <MobileBottomNav />
            <QuickAddFAB />
        </Box>
    );
};
